// Benepack OMS · 待审批提醒共享 hook + Provider (go-live 客户反馈 #3)
//   "需要审批的单据，应该有工作台提示，或者状态栏的提示"
//
// 三个消费面共享【一次取数】(Topbar 铃铛徽标 · 工作台「待我审批」卡 · Sidebar「审批中心」徽标) →
//   Provider 挂在 MainLayout (Topbar/Sidebar/Outlet 之上) · 单轮询 (不 3× poll)。
//
// 两条审批流 (RBAC 逐字对 permission_seed.sql 核实):
//   ① PURCHASE ORDER · 待审主状态 ∈ {pending_approval, in_approval} (= approvals.tsx APPROVAL_PENDING_STATUSES) ·
//      门控 purchase_order:approve (Sidebar permAny / routes.tsx /procurement/approvals 守门 / seed:89 ·
//      manager+admin :226) — ★ 用 purchase_order:approve 而非 approval:approve: 卡/铃铛跳 /procurement/approvals,
//      该 route 守 purchase_order:approve · 用同码门控避免把用户导向 403 (PO detail 内 step_approve 动作另由
//      approval:approve 门控 · 那是更细的实例级权限 · 此处面级提醒对齐路由可达性)。
//   ② QUOTATION · 待审状态 = submitted · 门控 quotation:approve (seed:71 · manager+admin :205)。
//
// 数据源 (承袭 approvals.tsx 数据源决策 · gateway list 只解析 page/size · 不支持服务端 status filter):
//   listPurchaseOrders/listQuotations({page:1, size:SUMMARY_SIZE}) 一次拉大页 → 客户端 status 过滤。
//   - 截断守卫: total > 已拉行数 → truncated=true (count 仍报过滤后的精确入队数 · UI 标 "≥")。
//   - 单流 RBAC: 无对应 approve 权限 → 不请求该流 (不触发 403) · 该流 count=0 items=[]。
//   - 单流失败: 仅该流 error (其余流照常) · 整体 loading 退出后徽标用已得数据。
//
// 刷新策略: 挂载即取 · 每 45s 轮询 · 路由变化 (loc.pathname) 再取一次 (用户操作后回看即时更新)。
//
// 本文件刻意共置 Provider + 消费 hook + 纯函数 + 常量 (共享取数契约的单一出处 · 同 AuthProvider 范式) →
// 关闭 react-refresh/only-export-components (HMR 回退整载可接受 · 非组件文件)。
/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { listPurchaseOrders, listQuotations } from '@/api/trade';
import { SUMMARY_SIZE } from '@/lib/money';
import type { PurchaseOrderListItem, QuotationListItem } from '@/types/views';

/** 审批面权限码 (逐字 permission_seed.sql · 见文件头)。 */
export const PO_APPROVE_PERM = 'purchase_order:approve';
export const QUOTATION_APPROVE_PERM = 'quotation:approve';

/** 轮询间隔 (ms) · 45s (提示词 ~30–60s 区间 · 不打爆 gateway per-IP burst)。 */
export const POLL_INTERVAL_MS = 45_000;

/** PO 待审主状态 (= approvals.tsx APPROVAL_PENDING_STATUSES · 短名)。 */
export const PO_PENDING_STATUSES = ['pending_approval', 'in_approval'] as const;
/** Quotation 待审状态 (submitted = 已提交待经理审批)。 */
export const QUOTATION_PENDING_STATUSES = ['submitted'] as const;

const PO_PENDING_SET = new Set<string>(PO_PENDING_STATUSES);
const QUOTATION_PENDING_SET = new Set<string>(QUOTATION_PENDING_STATUSES);

/** 待审条目类型 (铃铛下拉/卡片共用 · type 决定跳转目标 + 文案)。 */
export type PendingApprovalType = 'purchase_order' | 'quotation';

/** 单条待审条目 (铃铛下拉一行)。 */
export interface PendingApprovalItem {
  type: PendingApprovalType;
  id: number;
  /** 单号 (PoNo / QuotationNo)。 */
  no: string;
  /** 对手方 ID (PO=供应商 / Quotation=客户) · 0 = 无 (RefName 优雅降级)。 */
  counterpartyId: number;
  /** 金额 (numeric string · 原样 · UI 拼币种)。 */
  amount: string;
  /** 币种 (ISO 4217)。 */
  currency: string;
  /** 详情/审批页路由 (点击跳转)。 */
  to: string;
}

/** 纯函数: PO 大页 rows → 待审条目 (status 过滤 + 映射) · 供 vitest 直测。 */
export function poPendingItems(rows: PurchaseOrderListItem[]): PendingApprovalItem[] {
  return rows
    .filter((r) => PO_PENDING_SET.has(r.Status))
    .map((r) => ({
      type: 'purchase_order' as const,
      id: r.ID,
      no: r.PoNo,
      counterpartyId: r.SupplierID,
      amount: r.AmountTotal,
      currency: r.Currency,
      to: `/trade/purchase-orders/${r.ID}`,
    }));
}

/** 纯函数: Quotation 大页 rows → 待审条目 (status 过滤 + 映射) · 供 vitest 直测。 */
export function quotationPendingItems(rows: QuotationListItem[]): PendingApprovalItem[] {
  return rows
    .filter((r) => QUOTATION_PENDING_SET.has(r.Status))
    .map((r) => ({
      type: 'quotation' as const,
      id: r.ID,
      no: r.QuotationNo,
      counterpartyId: r.CustomerID,
      amount: r.AmountTotal,
      currency: r.Currency,
      to: `/trade/quotations/${r.ID}`,
    }));
}

/** 共享态 (三面消费)。 */
export interface PendingApprovalsState {
  /** 待审总数 (PO + Quotation · 过滤后精确入队数 · 截断时仍为已拉行的精确数)。 */
  count: number;
  /** PO 待审数 (Sidebar「审批中心」徽标专用 · 仅 PO 流)。 */
  poCount: number;
  /** 待审条目 (铃铛下拉 · PO 在前 Quotation 在后)。 */
  items: PendingApprovalItem[];
  /** 首次取数中 (徽标/卡 loading)。 */
  loading: boolean;
  /** 任一启用流的拉取行数 < 总数 → 计数可能少算 (UI 标 "≥")。 */
  truncated: boolean;
  /** 手动重取 (动作后即时刷新 · 可选)。 */
  refresh: () => void;
}

const PendingApprovalsContext = createContext<PendingApprovalsState | null>(null);

/**
 * PendingApprovalsProvider · 挂在 MainLayout (Topbar/Sidebar/Outlet 之上) → 单轮询共享给三面。
 * RBAC: 无任一 approve 权限的用户 → 不请求任何流 (count=0 · 三面均不显)。
 */
export function PendingApprovalsProvider({ children }: { children: ReactNode }) {
  const { hasPerm } = useAuth();
  const loc = useLocation();

  const canPo = hasPerm(PO_APPROVE_PERM);
  const canQuotation = hasPerm(QUOTATION_APPROVE_PERM);
  // 稳定 primitive 触发键 (★workbench 死循环教训: 不把派生对象/数组入 effect deps ·
  //   仅当【启用流集合】变化才重订阅轮询)。
  const streamKey = `${canPo ? 'po' : ''}|${canQuotation ? 'q' : ''}`;

  const [poItems, setPoItems] = useState<PendingApprovalItem[]>([]);
  const [quotationItems, setQuotationItems] = useState<PendingApprovalItem[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  // bump 计数器: refresh()/路由变化时自增 → 驱动取数 effect (与轮询解耦)。
  const [bump, setBump] = useState(0);
  const refresh = useCallback(() => setBump((b) => b + 1), []);

  // 取数: 并行拉两流 (各自 RBAC 门控) · 单流失败仅该流空 · 截断守卫合并。
  const fetchAll = useCallback(async () => {
    const tasks: Promise<void>[] = [];
    let nextTruncated = false;
    if (canPo) {
      tasks.push(
        listPurchaseOrders({ page: 1, size: SUMMARY_SIZE })
          .then((resp) => {
            const rows = resp.items ?? [];
            const total = resp.page?.total ?? rows.length;
            if (total > rows.length) nextTruncated = true;
            setPoItems(poPendingItems(rows));
          })
          .catch(() => setPoItems([])),
      );
    } else {
      setPoItems([]);
    }
    if (canQuotation) {
      tasks.push(
        listQuotations({ page: 1, size: SUMMARY_SIZE })
          .then((resp) => {
            const rows = resp.items ?? [];
            const total = resp.page?.total ?? rows.length;
            if (total > rows.length) nextTruncated = true;
            setQuotationItems(quotationPendingItems(rows));
          })
          .catch(() => setQuotationItems([])),
      );
    } else {
      setQuotationItems([]);
    }
    await Promise.all(tasks);
    setTruncated(nextTruncated);
    setLoading(false);
  }, [canPo, canQuotation]);

  // 挂载/启用流集合变化/bump → 取数 · 并起 45s 轮询。
  useEffect(() => {
    // 无任一权限 → 不请求 · 直接收尾 (loading=false · 三面不显)。
    if (!canPo && !canQuotation) {
      setPoItems([]);
      setQuotationItems([]);
      setTruncated(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const run = () => {
      if (!cancelled) void fetchAll();
    };
    run();
    // window.setInterval (非裸 setInterval): eslint globals 列入 setTimeout 未列 setInterval → 用 window. 前缀。
    const timer = window.setInterval(run, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
    // streamKey 是启用流集合的稳定代理 · bump 驱动手动/路由重取 · fetchAll 随 canPo/canQuotation 记忆化。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamKey, bump, fetchAll]);

  // 路由变化 → 重取一次 (用户操作后回看即时更新) · 仅当有启用流时。
  // ★跳过挂载首跑 (firstPathRun guard): 挂载首取已由上方 streamKey effect 负责 →
  //   本 effect 若也在挂载触发 setBump 会再驱动一次 fetchAll (挂载即双取 · 多余 2× gateway 请求 ·
  //   曾助推 per-IP 限流 429)。只在【后续】路由切换时重取。
  const firstPathRun = useRef(true);
  useEffect(() => {
    if (firstPathRun.current) {
      firstPathRun.current = false;
      return;
    }
    if (canPo || canQuotation) setBump((b) => b + 1);
    // 仅依赖 pathname (canPo/canQuotation 变化已由上方 streamKey effect 覆盖)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname]);

  const value = useMemo<PendingApprovalsState>(() => {
    const items = [...poItems, ...quotationItems];
    return {
      count: items.length,
      poCount: poItems.length,
      items,
      loading,
      truncated,
      refresh,
    };
  }, [poItems, quotationItems, loading, truncated, refresh]);

  return (
    <PendingApprovalsContext.Provider value={value}>{children}</PendingApprovalsContext.Provider>
  );
}

/**
 * usePendingApprovals · 三面消费共享待审态。
 * Provider 未挂 (理论不应发生 · MainLayout 恒挂) → 返回惰性空态 (徽标显 0 不 crash)。
 */
export function usePendingApprovals(): PendingApprovalsState {
  const ctx = useContext(PendingApprovalsContext);
  if (!ctx) {
    return {
      count: 0,
      poCount: 0,
      items: [],
      loading: false,
      truncated: false,
      refresh: () => {},
    };
  }
  return ctx;
}
